import type { ReactNode } from 'react'
import { Tooltip } from '@/components/Tooltip'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetContentBlock,
  ModalSheetContentIllustration,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
  ModalSheetHeaderShell,
  ModalSheetHeaderCloseButton,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import styles from './UpdateJobScopeDialog.module.css'
import { byDesign } from '@/infra/design'
import imgWelcomeUrl from '@/assets/misc/img_welcome.svg'
import CreateTeamIllustration from '@/components/SpotIllustration/CreateTeamIllustration'

export interface UpdateJobScopeDialogProps {
  showing: boolean
  isSubmitPending: boolean
  onClose: () => unknown
  children: ReactNode
}

export function UpdateJobScopeDialog({
  showing,
  isSubmitPending,
  onClose,
  children,
}: UpdateJobScopeDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
    >
      {children}
    </ModalSheet>
  )
}

export interface UpdateJobScopeItemsStepProps {
  isEmpty: boolean
  isDisabled: boolean
  onAddMore: () => unknown
  onClose: () => unknown
  onNext: () => unknown
  children: ReactNode
}

export function UpdateJobScopeItemsStep({
  isEmpty,
  isDisabled,
  onAddMore,
  onClose,
  onNext,
  children,
}: UpdateJobScopeItemsStepProps) {
  return (
    <>
      <UpdateJobScopeHeading title="Update job scope">
        <ItemHeadings />
      </UpdateJobScopeHeading>

      <GreyContainer>
        <ModalSheetContent>
          <div className={styles.itemsContainer}>
            {isEmpty && (
              <div className={styles.itemsEmptyState}>
                There are no service items on this job.
              </div>
            )}

            {children}
          </div>
        </ModalSheetContent>
      </GreyContainer>

      <ModalSheetFooter>
        <ModalSheetFooterButtonGroup>
          <Button
            label="Add More"
            onPress={onAddMore}
            testId="job-scope-update-add-more-button"
          />
        </ModalSheetFooterButtonGroup>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            onPress={onClose}
            testId="job-scope-update-close-button"
          />
          <Button
            label="Next"
            variant="primary"
            isDisabled={isDisabled}
            onPress={onNext}
            testId="job-scope-update-next-button"
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </>
  )
}

export interface UpdateJobScopeReasonStepProps {
  reasonField: ReactNode
  isDisabled: boolean
  isSubmitPending: boolean
  onBack: () => unknown
  onSubmit: () => unknown
}

export function UpdateJobScopeReasonStep({
  reasonField,
  isDisabled,
  isSubmitPending,
  onBack,
  onSubmit,
}: UpdateJobScopeReasonStepProps) {
  return (
    <>
      <UpdateJobScopeHeading title="Confirm updates" />

      <ModalSheetContent expandable>
        <ModalSheetContentBlock expanded>{reasonField}</ModalSheetContentBlock>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button label="Back" isDisabled={isSubmitPending} onPress={onBack} />
          <Button
            label="Submit"
            variant="primary"
            isDisabled={isDisabled}
            isPending={isSubmitPending}
            onPress={onSubmit}
            testId="job-scope-update-submit-button"
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </>
  )
}

export interface UpdateJobScopeSuccessStepProps {
  onClose: () => unknown
}

export function UpdateJobScopeSuccessStep({
  onClose,
}: UpdateJobScopeSuccessStepProps) {
  return (
    <>
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentIllustration
            src={imgWelcomeUrl}
            illustration={byDesign(undefined, CreateTeamIllustration)}
          />
          <ModalSheetContentTitle testId="job-scope-update-success">
            Job updates submitted
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            The job has been updated and all changes were applied to the job.
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            onPress={onClose}
            testId="job-scope-update-success-close-button"
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </>
  )
}

export interface UpdateJobScopeFailedStepProps {
  error: string
  isDisabled: boolean
  onClose: () => unknown
}

export function UpdateJobScopeFailedStep({
  error,
  isDisabled,
  onClose,
}: UpdateJobScopeFailedStepProps) {
  return (
    <>
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>
            Unable to update job scope
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            {error || (
              <p>
                Failure occurred while trying to update the job scope. Please
                try again.
              </p>
            )}
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button label="Cancel" isDisabled={isDisabled} onPress={onClose} />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </>
  )
}

interface UpdateJobScopeHeadingProps {
  title: string
  children?: ReactNode
}

function UpdateJobScopeHeading({
  title,
  children,
}: UpdateJobScopeHeadingProps) {
  return (
    <ModalSheetHeaderShell size="auto">
      <div className={styles.headerShell}>
        <div className={styles.header}>
          <div className={styles.title}>{title}</div>
          <div className={styles.closeButtonContainer}>
            <ModalSheetHeaderCloseButton />
          </div>
        </div>
        {children}
      </div>
    </ModalSheetHeaderShell>
  )
}

function ItemHeadings() {
  return (
    <div className={styles.listItem}>
      <span>Service item</span>

      <span className={styles.listItemCurrent}>
        Current &nbsp;
        <Tooltip
          content={'Current quantity for each item on the job.'}
          placement="top"
        >
          <Icon name="info" size={18} color="var(--blue400)" />
        </Tooltip>
      </span>
      <span className={styles.listItemNewTotal}>
        New total &nbsp;
        <Tooltip
          content={
            'New quantity that will replace the current quantity for each item.'
          }
          placement="top"
        >
          <Icon name="info" size={18} color="var(--blue400)" />
        </Tooltip>
      </span>
    </div>
  )
}

interface GreyContainerProps {
  children: ReactNode
}

function GreyContainer({ children }: GreyContainerProps) {
  return <div className={styles.greyBackround}>{children}</div>
}
